import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { TransactionFilters } from '@/components/customer-tabs';
import {
  CUSTOMER_SECTIONS,
  CustomerSectionBody,
  toSection,
} from '@/components/customers/customer-sections';
import {
  CustomerActions,
  CustomerBalances,
  CustomerChips,
  CustomerIdentity,
  customerName,
  useCustomerView,
} from '@/components/customers/customer-summary';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonSheet } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { useHas } from '@/lib/staff-context';
import { cn } from '@/lib/utils';

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional();

const searchSchema = z.object({
  // The old tab names (`wallets`, `verification`, …) still land somewhere.
  tab: z
    .unknown()
    .transform((value) => toSection(value) ?? 'overview')
    .optional(),
  kind: z.string().max(40).optional(),
  status: z.string().max(40).optional(),
  from: day,
  to: day,
});

/** One customer, one section at a time; the section and its filters live in the URL. */
export const Route = createFileRoute('/_app/customers/$customerId')({
  validateSearch: searchSchema,
  component: CustomerPage,
});

function CustomerPage() {
  const { customerId } = Route.useParams();
  // A new customer starts with nothing revealed.
  return <CustomerView key={customerId} customerId={customerId} />;
}

function CustomerView({ customerId }: Readonly<{ customerId: string }>) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const seesAudit = useHas('audit.all');
  const view = useCustomerView(customerId);
  const section = search.tab ?? 'overview';
  const sections = CUSTOMER_SECTIONS.filter(
    (item) => item.key !== 'audit' || seesAudit,
  );
  const filters: TransactionFilters = {
    kind: search.kind,
    status: search.status,
    from: search.from,
    to: search.to,
  };

  return (
    <QueryState
      query={view.profile}
      label="this customer"
      skeleton={<SkeletonSheet />}
    >
      {(profile) => (
        <div className="flex flex-col gap-4">
          <PageHeader
            title={customerName(profile, view.reveal)}
            description="Personal details are masked until revealed; every reveal is logged with its reason."
          />
          <div className="flex flex-col gap-3">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <CustomerIdentity profile={profile} reveal={view.reveal} />
            </p>
            <CustomerChips profile={profile} overview={view.overview.data} />
            <CustomerBalances overview={view.overview.data} />
            <CustomerActions
              profile={profile}
              overview={view.overview.data}
              reveal={view.reveal}
            />
          </div>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:gap-8">
            <nav
              aria-label="Customer sections"
              className="flex gap-1 overflow-x-auto border-b border-border xl:w-52 xl:shrink-0 xl:flex-col xl:overflow-visible xl:border-b-0"
            >
              {sections.map((item) => (
                <Link
                  key={item.key}
                  to="/customers/$customerId"
                  params={{ customerId }}
                  search={{ tab: item.key }}
                  aria-current={item.key === section ? 'page' : undefined}
                  className={cn(
                    'relative inline-flex h-9 shrink-0 items-center whitespace-nowrap px-3 text-base font-medium transition-colors xl:rounded-md',
                    'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full xl:after:hidden',
                    item.key === section
                      ? 'text-foreground after:bg-foreground xl:bg-accent'
                      : 'text-muted-foreground hover:text-foreground xl:hover:bg-accent/60',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="flex min-w-0 flex-1 flex-col gap-6">
              <CustomerSectionBody
                section={section}
                customerId={customerId}
                profile={profile}
                overview={view.overview.data}
                reveal={view.reveal}
                filters={filters}
                onFilters={(next) =>
                  void navigate({ search: { tab: 'money', ...next } })
                }
                onSection={(next) => void navigate({ search: { tab: next } })}
              />
            </div>
          </div>
        </div>
      )}
    </QueryState>
  );
}
